/**
 * The reveal, which is also the explanation screen (DESIGN.md §3.6).
 *
 * The completed ranking, in the ranking screen's own row shape — rank, 32px
 * avatar, name — but static: there is nothing to drag here, the answer is
 * settled. Ranks 1 and last are filled terracotta and carry the attribution
 * chip, because those are the only two slots anyone could score on.
 *
 * Both routes render this. `RevealScreen` passes `startAt` and the rows settle
 * off the server's clock; `JustificationScreen` passes none and the same list is
 * simply there. That is deliberate: the server's phase change cuts between two
 * routes mid-conversation, and the player must not see a screen change under
 * them while someone is talking.
 */
import { useMemo } from 'react';
import { motion } from 'motion/react';
import type { Attribution, PlayerId } from '@idgame/protocol';
import { LicenseCard, type LicensePlayer } from '../LicenseCard.js';
import { attributionChip, type AttributionMark } from './attribution.js';
import { REVEAL_SETTLE, revealElapsedMs, revealRowPlan } from './timeline.js';

/** Beyond this the chip stops naming people and starts counting them. */
const MAX_NAMED = 3;

export interface RevealBoardProps {
  ranking: readonly PlayerId[];
  players: ReadonlyMap<PlayerId, LicensePlayer>;
  selfId: PlayerId;
  /**
   * Who called the two ends. Absent on a phone that reconnected into the
   * explanation and never saw the reveal — it shows the order without chips
   * rather than inventing them.
   */
  attribution?: readonly Attribution[];
  /** Server epoch ms. Omit for a list that has already landed. */
  startAt?: number;
  /** serverNow − clientNow. Add to `Date.now()` for the server clock. */
  serverOffsetMs?: number;
}

export function RevealBoard({
  ranking,
  players,
  selfId,
  attribution,
  startAt,
  serverOffsetMs = 0,
}: RevealBoardProps) {
  const rows = useMemo(() => revealRowPlan(ranking.length), [ranking.length]);

  const names = useMemo(() => {
    const map = new Map<PlayerId, string>();
    for (const [id, player] of players) map.set(id, player.name);
    return map;
  }, [players]);

  const byPlayer = useMemo(() => {
    const map = new Map<PlayerId, Attribution>();
    for (const entry of attribution ?? []) map.set(entry.playerId, entry);
    return map;
  }, [attribution]);

  // A phone that joins late — or one rendering the same board after the cut to
  // justification — gets the finished list, not a replay of an animation the
  // room has already had.
  const elapsed = startAt === undefined ? Infinity : revealElapsedMs(startAt, serverOffsetMs);

  return (
    // Tighter than the ranking screen's list on purpose: nothing here is a
    // touch target, and twelve rows plus the rating bar and End turn have to
    // land on one 844px screen without the room having to scroll to argue.
    <ol className="space-y-1" data-testid="reveal-list">
      {rows.map((row) => {
        const id = ranking[row.index];
        const player = id ? players.get(id) : undefined;
        if (!id || !player) return null;

        const marks = row.end
          ? attributionChip({
              end: row.end,
              attribution: byPlayer.get(id),
              names,
              selfId,
            })
          : [];
        const settled = elapsed >= row.delayMs + REVEAL_SETTLE.rowDurationMs;

        return (
          <motion.li
            key={id}
            data-testid={`reveal-row-${id}`}
            data-rank={row.rank}
            initial={settled ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: REVEAL_SETTLE.rowDurationMs / 1000,
              delay: Math.max(0, row.delayMs - Math.max(0, elapsed)) / 1000,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <LicenseCard
              player={{ ...player, isSelf: id === selfId }}
              variant="chip"
              rank={row.rank}
              tone={row.end === 'first' ? 'top' : row.end === 'last' ? 'bottom' : 'neutral'}
            >
              {marks.length > 0 && <AttributionChip id={id} marks={marks} />}
            </LicenseCard>
          </motion.li>
        );
      })}
    </ol>
  );
}

/**
 * `Leo ✓ · Nina ✓ · You ✓`, right-aligned inside the row.
 *
 * It rides on a terracotta fill, so the ticks are cream like the name rather
 * than green: a second colour in a 10px chip reads as noise at arm's length,
 * and the glyph already carries the meaning. Long lists stop naming people —
 * eleven names would push the name they annotate off the row.
 */
function AttributionChip({ id, marks }: { id: PlayerId; marks: readonly AttributionMark[] }) {
  const named = marks.slice(0, MAX_NAMED);
  const rest = marks.length - named.length;
  return (
    <span
      data-testid={`attribution-${id}`}
      className="shrink-0 text-[0.625rem] whitespace-nowrap text-cream/85"
    >
      {named.map((mark, index) => (
        <span key={mark.playerId}>
          {index > 0 && ' · '}
          {mark.name} {mark.correct ? '✓' : '✗'}
        </span>
      ))}
      {rest > 0 && ` · +${rest}`}
    </span>
  );
}

/** The roster, in the shape the board wants it. */
export function toLicenseMap(
  players: readonly LicensePlayer[],
): ReadonlyMap<PlayerId, LicensePlayer> {
  const map = new Map<PlayerId, LicensePlayer>();
  for (const player of players) {
    map.set(player.id, {
      id: player.id,
      name: player.name,
      photo: player.photo,
      connected: player.connected,
    });
  }
  return map;
}
